import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "react-bootstrap";
import {
  apiBase,
  customerStatusLabel,
  dateLabel,
  reviewStatusLabel,
} from "./api";
import { usePortal } from "../Portal/PortalContext";
import "./reviews.css";
import { ArrowUpRight, Bell, Check } from "lucide-react";
async function notifications(path = "", method = "GET") {
  const r = await fetch(`${apiBase}/api/notifications${path}`, {
    method,
    credentials: "include",
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.message || "Could not load notifications");
  return d;
}
export function useUnreadUpdates(enabled: boolean) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let active = true;
    setUnread(0);
    if (!enabled) return;
    const load = () => {
      if (document.visibilityState === "visible")
        notifications()
          .then(d => {
            if (active) setUnread(d.unread);
          })
          .catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("review-notifications-changed", load);
    return () => {
      active = false;
      clearInterval(t);
      window.removeEventListener("review-notifications-changed", load);
    };
  }, [enabled]);
  return unread;
}
export function reviewNotificationLink(n: any) {
  const target =
    n.target ||
    (n.kind === "answer_delivered"
      ? `answer:${n.event_key?.slice(7) || ""}`
      : n.kind.startsWith("proposal_")
        ? "proposal:"
        : "discussion");
  const [kind, id] = target.split(":");
  const section =
    kind === "answer"
      ? "answers"
      : kind === "proposal"
        ? "proposal"
        : "discussion";
  return `/reviews/${n.review_id}?section=${section}${id ? `&target=${encodeURIComponent(kind === "proposal" ? id : `${kind}-${id}`)}` : ""}`;
}
export default function Notifications() {
  const [data, setData] = useState<any>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [unreadOnly, setUnreadOnly] = useState(true);
  const navigate = useNavigate();
  // Customers get a plain heading; staff keep the workflow description.
  const customer = usePortal().access?.accountType === "customer";
  const load = () =>
    notifications()
      .then(setData)
      .catch(e => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  async function open(n: any) {
    setBusy(true);
    setError("");
    try {
      await notifications(`/${n.id}/read`, "PATCH");
      window.dispatchEvent(new Event("review-notifications-changed"));
      navigate(
        (n.kind === "assignment_queue" || n.kind.startsWith("admin_"))
          ? "/admin/reviews"
          : reviewNotificationLink(n)
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function markRead(n: any) {
    setBusy(true);
    setError("");
    try {
      await notifications(`/${n.id}/read`, "PATCH");
      setData((old: any) => ({
        ...old,
        notifications: old.notifications.map((x: any) =>
          x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x
        ),
      }));
      window.dispatchEvent(new Event("review-notifications-changed"));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function markAllRead() {
    setBusy(true);
    setError("");
    try {
      await notifications("/read-all", "PATCH");
      const now = new Date().toISOString();
      setData((old: any) => ({
        ...old,
        unread: 0,
        notifications: old.notifications.map((x: any) => ({
          ...x,
          read_at: x.read_at || now,
        })),
      }));
      window.dispatchEvent(new Event("review-notifications-changed"));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function more() {
    if (data?.nextOffset == null || busy) return;
    setBusy(true);
    try {
      const next = await notifications(`?offset=${data.nextOffset}`);
      setData((old: any) => {
        // Every event for a review should show the newest status we fetched.
        const latest = new Map<string, any>(
          next.notifications.map((n: any) => [n.review_id, n])
        );
        const previous = old.notifications.map((n: any) => {
          const fresh = latest.get(n.review_id);
          return fresh
            ? {
                ...n,
                review_status: fresh.review_status,
                review_closed_at: fresh.review_closed_at,
                review_proposal_state: fresh.review_proposal_state,
                review_payment_state: fresh.review_payment_state,
              }
            : n;
        });
        return {
          ...next,
          notifications: Array.from(
            new Map(
              [...previous, ...next.notifications].map((n: any) => [n.id, n])
            ).values()
          ),
        };
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const unreadCount = data
    ? data.notifications.filter((n: any) => !n.read_at).length
    : 0;
  const shown = data
    ? data.notifications.filter((n: any) => !unreadOnly || !n.read_at)
    : [];
  return (
    <main className="reviewed-documents review-page">
      <header className="reviewed-documents-heading">
        <div>
          {customer ? (
            <h1>Notifications</h1>
          ) : (
            <>
              <p className="reviewed-eyebrow">IN-APP NOTIFICATIONS</p>
              <h1>Your updates</h1>
              <p>
                Proposals, assignments, written answers, and client–attorney
                follow-up. Opening a review marks its updates as read.
              </p>
            </>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="outline-dark" disabled={busy} onClick={markAllRead}>
            <Check size={13} /> Mark all as read
          </Button>
        )}
      </header>
      {error && <Alert variant="danger">{error}</Alert>}
      {data?.notifications.length > 0 && (
        <p className="text-secondary">
          Updates describe past activity. Each review's current status is shown
          with its title.
        </p>
      )}
      {data && data.notifications.length > 0 && (
        <nav className="attorney-queue-tabs" aria-label="Which updates">
          <button
            type="button"
            aria-pressed={unreadOnly}
            onClick={() => setUnreadOnly(true)}
          >
            Unread {unreadCount > 0 && <span>{unreadCount}</span>}
          </button>
          <button
            type="button"
            aria-pressed={!unreadOnly}
            onClick={() => setUnreadOnly(false)}
          >
            All
          </button>
        </nav>
      )}
      {!data ? (
        <p>Loading updates…</p>
      ) : !shown.length ? (
        <section className="reviewed-empty">
          <h2>You’re up to date</h2>
          <p>
            {data.notifications.length
              ? "No unread updates. Choose All to see earlier ones."
              : "New review activity will appear here."}
          </p>
        </section>
      ) : (
        <div className="review-notifications">
          {shown.map((n: any, index: number) => (
            <div className="notification-item" key={n.id}>
              {(index === 0 || shown[index - 1].review_id !== n.review_id) && (
                <div className="notification-matter-heading">
                  <h2 className="notification-matter-title">
                    {n.review_title || "Review update"}
                  </h2>
                  {n.review_status && (
                    <span className="notification-current-status">
                      Current status:{" "}
                      {(customer ? customerStatusLabel : reviewStatusLabel)({
                        status: n.review_status,
                        closed_at: n.review_closed_at,
                        proposal_state: n.review_proposal_state,
                        payment_state: n.review_payment_state,
                      })}
                    </span>
                  )}
                </div>
              )}
              <div className="notification-row">
                <button
                  disabled={busy}
                  onClick={() => open(n)}
                  className={n.read_at ? "read" : "unread"}
                >
                  <Bell size={16} strokeWidth={1.5} />
                  <div>
                    <strong>{n.title}</strong>
                    {n.preview && (
                      <p className="notification-preview">
                        {n.actor_name ? `${n.actor_name}: ` : ""}
                        {n.preview}
                      </p>
                    )}
                    <span>{dateLabel(n.created_at)}</span>
                  </div>
                  <ArrowUpRight size={14} />
                </button>
                {!n.read_at && (
                  <button
                    type="button"
                    className="notification-mark"
                    aria-label={`Mark “${n.title}” as read`}
                    title="Mark as read"
                    disabled={busy}
                    onClick={() => markRead(n)}
                  >
                    <Check size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {data?.nextOffset != null && (
        <Button variant="light" disabled={busy} onClick={more}>
          Load older updates
        </Button>
      )}
    </main>
  );
}
